import React, { useState } from 'react';
import {
    Box,
    Stack,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { AssignmentRounded, ClearRounded } from '@mui/icons-material';
import { useBulkCreateUserAssignmentsMutation, useBulkDeleteUserAssignmentsMutation } from '../../../../api/apiSlice';
import { cardSlice } from '../../../../api/cardSlice';
import ToggleButtons from '../../../CustomComponents/ToggleButtons';

const BulkReviewerAssignment = ({ 
    selectedCards, 
    reviewerOptions = [], 
    clearSelection 
}) => {
    const [actionType, setActionType] = useState('assign'); // 'assign' or 'unassign'
    const [selectedReviewers, setSelectedReviewers] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [result, setResult] = useState(null);
    const dispatch = useDispatch();
    
    const [bulkCreateUserAssignments] = useBulkCreateUserAssignmentsMutation();
    const [bulkDeleteUserAssignments] = useBulkDeleteUserAssignmentsMutation();

    const handleReviewerChange = (event) => {
        setSelectedReviewers(event.target.value);
    };

    const handleRemoveReviewer = (reviewerId) => {
        setSelectedReviewers(prev => prev.filter(id => id !== reviewerId));
    };

    const handleSubmit = async () => {
        if (selectedCards.size === 0 || selectedReviewers.length === 0) return;

        setIsProcessing(true);
        setResult(null);

        try {
            const cardIds = Array.from(selectedCards);
            const assignments = [];

            cardIds.forEach(cardId => {
                selectedReviewers.forEach(reviewerId => {
                    assignments.push({
                        cardId: cardId,
                        reviewerId: reviewerId
                    });
                });
            });
            const response = actionType === 'assign'
                ? await bulkCreateUserAssignments({ assignments }).unwrap()
                : await bulkDeleteUserAssignments({ assignments }).unwrap();

            let successful = 0, failed = 0;
            response.results.forEach(result => {
                if (result.status === 'success') successful++;
                else failed++;
            });
            const message = `${actionType === 'assign' ? 'Assigned' : 'Unassigned'} reviewers ${actionType === 'assign' ? 'to' : 'from'} ${cardIds.length} cards. ${successful} operations successful${failed > 0 ? ', ' + failed + ' failed' : ''}.`;

            setResult({
                type: failed > 0 ? 'error' : 'success',
                message
            });

            setSelectedReviewers([]);
            clearSelection();
        } catch (error) {
            console.error('Bulk assignment error:', error);
            setResult({
                type: 'error',
                message: `Failed to ${actionType} reviewers. Please try again.`
            });
        } finally {
            setIsProcessing(false);
            dispatch(cardSlice.util.invalidateTags(['Card']));
        }
    };

    return (
        <Stack spacing={2} p={2} backgroundColor="white" boxShadow={2} borderRadius={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="heavy" fontSize="20px" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentRounded />
                    Bulk Assign
                </Typography>
                <ToggleButtons
                    options={[
                        { label: 'Assign Reviewers', value: 'assign' },
                        { label: 'Unassign Reviewers', value: 'unassign' }
                    ]}
                    toggleValue={actionType}
                    setToggleValue={setActionType}
                    logUserAction={() => {}} // No logging needed for this component
                />
                <Button
                    size="small"
                    variant="outlined"
                    onClick={clearSelection}
                    startIcon={<ClearRounded />}
                >
                    Clear Selection
                </Button>
            </Stack>

            {/* Reviewer Selection and Action Button */}
            <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Select Reviewers</InputLabel>
                    <Select
                        multiple
                        value={selectedReviewers}
                        onChange={handleReviewerChange}
                        input={<OutlinedInput label="Select Reviewers" />}
                        disabled={isProcessing}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((reviewerId) => {
                                    const reviewer = reviewerOptions.find(r => r.value === reviewerId);
                                    return (
                                        <Chip 
                                            key={reviewerId} 
                                            label={reviewer?.label || reviewerId}
                                            size="small"
                                            onDelete={() => handleRemoveReviewer(reviewerId)}
                                            onMouseDown={(event) => event.stopPropagation()}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    >
                        {reviewerOptions.map((reviewer) => (
                            <MenuItem key={reviewer.value} value={reviewer.value}>
                                {reviewer.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isProcessing || selectedReviewers.length === 0 || selectedCards.size === 0}
                    startIcon={isProcessing ? <CircularProgress size={16} /> : <AssignmentRounded />}
                    size="large"
                    sx={{ px: 3, minWidth: 120 }}
                >
                    {isProcessing 
                        ? `${actionType === 'assign' ? 'Assigning' : 'Unassigning'}...`
                        : `${actionType === 'assign' ? 'Assign' : 'Unassign'} Reviewers`
                    }
                </Button>
            </Stack>

            {/* Result Message */}
            {result && (
                <Alert 
                    severity={result.type} 
                    onClose={() => setResult(null)}
                    sx={{ fontSize: '0.875rem' }}
                >
                    {result.message}
                </Alert>
            )}
        </Stack>
    );
};

export default BulkReviewerAssignment;